/**
 * Vague 15 — Composants additifs
 *  • <FrictionHeatmap />          — Heatmap agrégée (Origination)
 *  • <DoctrineTestButton />        — Bouton "Tester contre la doctrine"
 *  • <DoctrineDiffViewer />        — Diff git-style entre 2 révisions
 *
 * Vague 17 — Drill-down de la heatmap
 *  • <FrictionCellDrillDown />     — Panneau latéral d'une cellule
 *  • Cellules cliquables + bouton "Lancer plan d'action" inline
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Flame, ScrollText, CheckCircle, AlertTriangle, X as XIcon,
  Loader2, GitCompare, Plus, Minus, ArrowRight, ListChecks, ExternalLink, ChevronRight,
  Ghost, Eye, EyeOff, RefreshCw,
} from "lucide-react";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";

// ============================================================================
// 1. Heatmap des frictions
// ============================================================================
const CATEGORIES_LABEL = {
  LTV: "LTV",
  acteur: "Acteur",
  reglementaire: "Réglementaire",
  dossier: "Dossier",
  juridique: "Juridique",
  timing: "Timing",
  autre: "Autre",
};
const LEVEL_LABEL = {
  level_1: "N1 Micro",
  level_2: "N2 Spéc.",
  level_3: "N3 S.-sect.",
  level_4: "N4 Secteur",
  level_5: "N5 Macro",
  opportunite: "Opp.",
};

export function FrictionHeatmap() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // Vague 17 — drill-down
  const [activeCell, setActiveCell] = useState(null); // { categorie, level }
  // Vague 18 — couche fantômes overlay (lazy load)
  const [phantomLayer, setPhantomLayer] = useState(null);
  const [phantomLayerVisible, setPhantomLayerVisible] = useState(false);
  const [phantomLoading, setPhantomLoading] = useState(false);

  const load = (silent = false) => {
    if (!silent) setLoading(true);
    api.get("/workspace/origination/frictions/heatmap")
      .then((r) => setData(r.data))
      .catch((e) => setError(fmtError(e, "Erreur")))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // V23.16.x — Mise à jour interne + externe (async : NOAM croise workspace + signaux externes)
  const [ctxRefreshing, setCtxRefreshing] = useState(false);
  const contextRefresh = async () => {
    setCtxRefreshing(true);
    try {
      await api.post("/workspace/origination/frictions/context-refresh");
      notify.success("Mise à jour lancée — Noam croise vos données internes (pipeline, plans, risques) et les signaux externes (veille, radar). La carte se rafraîchira automatiquement.");
      let n = 0;
      const iv = setInterval(() => {
        n += 1;
        load(true);
        if (n >= 14) clearInterval(iv);
      }, 12000);
    } catch (e) {
      notify.error(fmtError(e, "Mise à jour impossible"));
    } finally {
      setCtxRefreshing(false);
    }
  };

  // Vague 18 — Toggle de la couche fantômes (lazy load au 1er clic)
  const togglePhantomLayer = async () => {
    if (phantomLayerVisible) {
      setPhantomLayerVisible(false);
      return;
    }
    if (phantomLayer) {
      setPhantomLayerVisible(true);
      return;
    }
    setPhantomLoading(true);
    try {
      const r = await api.get("/workspace/cartographie/phantoms/aggregate");
      setPhantomLayer(r.data);
      setPhantomLayerVisible(true);
    } catch (e) {
      notify.error(fmtError(e, "Couche fantômes indisponible"));
    } finally {
      setPhantomLoading(false);
    }
  };

  const maxCount = useMemo(() => {
    if (!data?.matrix) return 0;
    let m = 0;
    for (const c of Object.keys(data.matrix)) {
      for (const l of Object.keys(data.matrix[c])) {
        m = Math.max(m, data.matrix[c][l].count || 0);
      }
    }
    return m;
  }, [data]);

  if (loading) return <div className="text-zinc-500 text-sm"><Loader2 className="w-3 h-3 animate-spin inline mr-1" /> Chargement…</div>;
  if (error) return <div className="text-red-400 text-xs">{error}</div>;
  if (!data) return null;

  const cellColor = (count) => {
    if (!maxCount || count === 0) return "bg-white/[0.02] border-white/5 text-zinc-600";
    const ratio = count / maxCount;
    if (ratio >= 0.66) return "bg-[#EF4444]/30 border-[#EF4444]/50 text-red-300";
    if (ratio >= 0.33) return "bg-[#EF9F27]/25 border-[#EF9F27]/45 text-[#EF9F27]";
    return "bg-[#F4D35E]/15 border-[#F4D35E]/30 text-[#F4D35E]";
  };

  return (
    <div className="space-y-5" data-testid="orig-friction-heatmap">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 noam-overline text-[#EF9F27]">
            <Flame className="w-3 h-3" /> Carte des frictions · agrégée
          </div>
          <h2 className="noam-display text-xl mt-1">Où s'accumulent les obstacles</h2>
          <p className="text-zinc-400 text-xs mt-1 max-w-2xl">
            Heatmap des frictions identifiées par NOAM. Croisement catégorie × niveau fractale (incluant les opportunités).
            Plus la cellule est rouge, plus les obstacles s'accumulent dans cette zone — c'est l'ennemi à traiter.
            La carte se met à jour depuis vos données internes ET les signaux externes.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={contextRefresh}
            disabled={ctxRefreshing}
            className="text-[10px] px-2 py-1 rounded border border-[#1D9E75]/40 text-[#1D9E75] hover:bg-[#1D9E75]/10 inline-flex items-center gap-1 disabled:opacity-50"
            data-testid="heatmap-context-refresh"
            title="NOAM croise vos données internes (pipeline, plans, risques, incidents) et les signaux externes (veille, radar) pour mettre à jour la carte"
          >
            {ctxRefreshing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            Mise à jour interne + externe
          </button>
          <button
            onClick={togglePhantomLayer}
            disabled={phantomLoading}
            className={`text-[10px] px-2 py-1 rounded border inline-flex items-center gap-1 transition ${
              phantomLayerVisible ? "border-[#A78BFA]/50 bg-[#A78BFA]/15 text-[#A78BFA]" : "border-white/10 text-zinc-400 hover:text-[#A78BFA]"
            }`}
            data-testid="heatmap-phantom-toggle"
            title="Superposer la couche fantômes (Vague 18)"
          >
            {phantomLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Ghost className="w-3 h-3" />}
            Fantômes
          </button>
          <button onClick={() => load()} className="text-[10px] px-2 py-1 rounded border border-white/10 text-zinc-300" data-testid="heatmap-refresh">
            Rafraîchir
          </button>
        </div>
      </div>

      {data.total_frictions === 0 ? (
        <div className="noam-card p-8 text-center" data-testid="heatmap-empty">
          <Flame className="w-8 h-8 text-zinc-600 mx-auto" />
          <div className="text-sm text-zinc-300 mt-3">Aucune friction encore enregistrée</div>
          <p className="text-xs text-zinc-500 mt-2 max-w-md mx-auto">
            Lancez quelques analyses <strong>Friction Gap</strong> (bouton orange depuis le Pipeline ou l'onglet Fractale ↑)
            pour alimenter cette carte. Chaque analyse est automatiquement persistée et agrégée ici.
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            <KPI label="Frictions totales" value={data.total_frictions} color="#EF9F27" />
            <KPI label="Analyses enreg." value={data.total_snapshots} color="#378ADD" />
            <KPI
              label="Zone critique"
              value={data.top_friction ? `${CATEGORIES_LABEL[data.top_friction.categorie] || data.top_friction.categorie} × ${LEVEL_LABEL[data.top_friction.level] || data.top_friction.level}` : "—"}
              color="#EF4444"
              testid="heatmap-top-friction"
            />
            <KPI
              label="Zone coûteuse"
              value={data.top_cost_cell ? `${CATEGORIES_LABEL[data.top_cost_cell.categorie] || data.top_cost_cell.categorie} × ${LEVEL_LABEL[data.top_cost_cell.level] || data.top_cost_cell.level}` : "—"}
              color="#A78BFA"
              testid="heatmap-top-cost"
            />
            <KPI
              label="Coût mensuel"
              value={data.total_cost_monthly_meur ? `${data.total_cost_monthly_meur.toFixed(2)} M€` : "—"}
              color="#EF4444"
              testid="heatmap-cost-monthly"
            />
            <KPI
              label="Coût annuel projeté"
              value={data.total_cost_annual_meur ? `${data.total_cost_annual_meur.toFixed(1)} M€` : "—"}
              color="#EF4444"
              testid="heatmap-cost-annual"
            />
          </div>

          <div className="noam-card p-4 overflow-x-auto" data-testid="heatmap-matrix">
            <div className="flex items-center justify-between mb-2 text-[10px] text-zinc-500">
              <span>Clic sur une cellule active pour ouvrir le détail et lancer un plan d'action.</span>
            </div>
            <table className="w-full text-xs">
              <thead>
                <tr className="text-zinc-500 text-[10px] uppercase tracking-wider border-b border-white/10">
                  <th className="text-left p-2 sticky left-0 bg-[var(--noam-card-bg)] z-10">Catégorie ↓ / Niveau →</th>
                  {data.levels.map((l) => (
                    <th key={l} className="text-center p-2 min-w-[70px]">{LEVEL_LABEL[l] || l}</th>
                  ))}
                  <th className="text-center p-2 text-[#7F77DD] min-w-[60px]">Total</th>
                </tr>
              </thead>
              <tbody>
                {data.categories.map((cat) => (
                  <tr key={cat}>
                    <td className="p-2 text-zinc-300 font-medium sticky left-0 bg-[var(--noam-card-bg)] z-10">{CATEGORIES_LABEL[cat] || cat}</td>
                    {data.levels.map((l) => {
                      const cell = data.matrix[cat][l];
                      const isClickable = cell.count > 0;
                      return (
                        <td
                          key={`${cat}-${l}`}
                          onClick={isClickable ? () => setActiveCell({ categorie: cat, level: l }) : undefined}
                          className={`p-2 text-center border ${cellColor(cell.count)} transition ${isClickable ? "cursor-pointer hover:ring-2 hover:ring-[#7F77DD]/60 hover:ring-offset-1 hover:ring-offset-[var(--noam-card)]" : ""}`}
                          title={cell.count > 0 ? `${cell.count} friction(s) · score moyen ${cell.avg_score} · clic pour détail` : "Aucune"}
                          data-testid={`heatmap-cell-${cat}-${l}`}
                        >
                          {cell.count > 0 ? (
                            <div>
                              <div className="font-bold">{cell.count}</div>
                              <div className="text-[9px] opacity-70">⌀ {cell.avg_score}</div>
                              {cell.cost_monthly > 0 && (
                                <div className="text-[9px] text-zinc-300 opacity-90">{cell.cost_monthly.toFixed(2)}M€</div>
                              )}
                            </div>
                          ) : <span className="opacity-30">·</span>}
                        </td>
                      );
                    })}
                    <td className="p-2 text-center text-[#7F77DD] font-bold border border-white/5">{data.by_categorie[cat] || 0}</td>
                  </tr>
                ))}
                <tr className="border-t border-white/10">
                  <td className="p-2 text-zinc-500 text-[10px] uppercase tracking-wider sticky left-0 bg-[var(--noam-card-bg)] z-10">Total</td>
                  {data.levels.map((l) => (
                    <td key={`total-${l}`} className="p-2 text-center text-[#7F77DD] font-bold">
                      {data.by_level[l] || 0}
                    </td>
                  ))}
                  <td className="p-2 text-center text-[#7F77DD] font-bold">{data.total_frictions}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {data.snapshots && data.snapshots.length > 0 && (
            <div data-testid="heatmap-recent">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Analyses récentes (top 10)</div>
              <div className="space-y-1">
                {data.snapshots.slice(0, 10).map((s) => (
                  <div key={s.id} className="text-[11px] flex items-center gap-2 p-2 bg-white/[0.02] border border-white/5 rounded">
                    <span className="text-[#EF9F27] font-bold w-6 text-center">{s.score_friction || "?"}</span>
                    <span className="text-zinc-400 flex-shrink-0">{s.scope === "opportunite" ? "Opp" : `N${s.fractale_level}`}</span>
                    <span className="text-zinc-300 flex-1 truncate" title={s.synthese}>{s.synthese}</span>
                    <span className="text-zinc-600">{s.frictions_count} fr.</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {activeCell && (
        <FrictionCellDrillDown
          categorie={activeCell.categorie}
          level={activeCell.level}
          onClose={() => setActiveCell(null)}
          onPlanCreated={() => { load(); }}
        />
      )}

      {/* Vague 18 — Couche fantômes superposée */}
      {phantomLayerVisible && phantomLayer && (
        <PhantomOverlayLayer data={phantomLayer} onClose={() => setPhantomLayerVisible(false)} />
      )}
    </div>
  );
}

function KPI({ label, value, color, testid }) {
  return (
    <div className="noam-card p-3" data-testid={testid}>
      <div className="text-[9px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className="text-base font-bold mt-1 truncate" style={{ color }}>{value}</div>
    </div>
  );
}

function topKey(dict, label) {
  if (!dict) return "—";
  let best = null, max = 0;
  for (const k of Object.keys(dict)) {
    if (dict[k] > max) { max = dict[k]; best = k; }
  }
  return best ? (label[best] || best) : "—";
}

// ============================================================================
// 2. Test contre la doctrine — Bouton + Modal
// ============================================================================
const VERDICT_META = {
  conforme: { color: "#1D9E75", icon: CheckCircle, label: "Conforme" },
  en_tension: { color: "#EF9F27", icon: AlertTriangle, label: "En tension" },
  contradictoire: { color: "#EF4444", icon: XIcon, label: "Contradictoire" },
};

export function DoctrineTestButton({ sourceModule, sourceId, label, getText, compact = false }) {
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState("");

  const start = () => {
    setOpen(true); setResult(null);
    setText(typeof getText === "function" ? (getText() || "") : "");
  };
  const run = async () => {
    const value = (text || "").trim();
    if (value.length < 10) { notify.error("Le raisonnement doit faire au moins 10 caractères."); return; }
    setLoading(true);
    try {
      const r = await api.post("/workspace/doctrines/test-reasoning", {
        text: value,
        source_module: sourceModule,
        source_id: sourceId,
        label,
      });
      setResult(r.data);
    } catch (e) {
      notify.error(fmtError(e, "Erreur"));
    } finally { setLoading(false); }
  };

  return (
    <>
      <button
        onClick={start}
        className={`text-[10px] px-2 py-1 rounded border border-[#7F77DD]/40 bg-[#7F77DD]/10 text-[#7F77DD] hover:bg-[#7F77DD]/20 inline-flex items-center gap-1 ${compact ? "" : ""}`}
        data-testid={`doctrine-test-btn${sourceId ? `-${sourceId}` : ""}`}
        title="Tester ce raisonnement contre la doctrine active"
      >
        <ScrollText className="w-3 h-3" />
        Tester / Doctrine
      </button>
      {open && (
        <DoctrineTestModal
          text={text}
          setText={setText}
          loading={loading}
          result={result}
          onRun={run}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

function DoctrineTestModal({ text, setText, loading, result, onRun, onClose }) {
  const verdict = result?.verdict;
  const meta = verdict ? VERDICT_META[verdict] : null;
  const Icon = meta?.icon || ScrollText;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="doctrine-test-modal">
      <div className="noam-card max-w-2xl w-full max-h-[92vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <ScrollText className="w-4 h-4 text-[#7F77DD]" />
            <h3 className="text-base font-semibold text-zinc-100">Test contre la doctrine</h3>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white/10 rounded" data-testid="doctrine-test-close">
            <XIcon className="w-4 h-4 text-zinc-400" />
          </button>
        </div>

        {!result && (
          <div className="space-y-3">
            <p className="text-xs text-zinc-400">
              Collez ici une décision, un raisonnement, une recommandation, un argument de comité — NOAM
              vérifiera la cohérence avec la doctrine active.
            </p>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Ex: « Nous devons accepter le mandat même sans collatéral parce que le client est connu et qu'on veut renforcer la relation. »"
              rows={8}
              className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:border-[#7F77DD] focus:outline-none resize-none"
              data-testid="doctrine-test-textarea"
            />
            <div className="text-[10px] text-zinc-500 text-right">{text.length} caractères (min 10)</div>
            <button
              onClick={onRun}
              disabled={loading || text.length < 10}
              className="btn-primary w-full disabled:opacity-40"
              data-testid="doctrine-test-run"
            >
              {loading ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Analyse NOAM…</> : "Tester contre la doctrine"}
            </button>
          </div>
        )}

        {result && (
          <div className="space-y-4" data-testid="doctrine-test-result">
            <div className="flex items-center gap-3">
              <div
                className="w-16 h-16 rounded-full border-4 flex items-center justify-center flex-shrink-0"
                style={{ borderColor: meta?.color || "#7F77DD" }}
              >
                <Icon className="w-7 h-7" style={{ color: meta?.color || "#7F77DD" }} />
              </div>
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-wider text-zinc-500">Verdict</div>
                <div className="text-lg font-bold" style={{ color: meta?.color || "#7F77DD" }} data-testid="doctrine-test-verdict">
                  {meta?.label || result.verdict}
                </div>
                <div className="text-[10px] text-zinc-500">
                  Conformité : <strong style={{ color: meta?.color || "#7F77DD" }}>{result.score_conformite ?? "?"}/100</strong>
                  {result.doctrine_version && <> · Doctrine v{result.doctrine_version}</>}
                </div>
              </div>
            </div>

            <div className="text-xs text-zinc-300 bg-white/[0.02] border border-white/5 rounded p-3">
              {result.synthese}
            </div>

            {result.principles_respected?.length > 0 && (
              <Section title="Principes respectés" color="#1D9E75" items={result.principles_respected.map((p) => ({ left: p }))} testid="dt-respected" />
            )}
            {result.principles_in_tension?.length > 0 && (
              <Section title="Principes en tension" color="#EF9F27" items={result.principles_in_tension.map((p) => ({ left: p.principe, right: p.tension }))} testid="dt-tension" />
            )}
            {result.principles_contradicted?.length > 0 && (
              <Section title="Principes contredits" color="#EF4444" items={result.principles_contradicted.map((p) => ({ left: p.principe, right: p.contradiction }))} testid="dt-contradicted" />
            )}
            {result.principles_ignored?.length > 0 && (
              <Section title="Principes oubliés" color="#A78BFA" items={result.principles_ignored.map((p) => ({ left: p }))} testid="dt-ignored" />
            )}

            {result.recommendation && (
              <div className="border-l-2 border-[#7F77DD] pl-3 text-xs">
                <div className="text-[10px] uppercase tracking-wider text-[#7F77DD] mb-1">Recommandation</div>
                <div className="text-zinc-300">{result.recommendation}</div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Section({ title, color, items, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider mb-2" style={{ color }}>{title}</div>
      <div className="space-y-1.5">
        {items.map((it, i) => (
          <div key={i} className="text-xs bg-white/[0.02] border border-white/5 rounded p-2">
            <div className="text-zinc-200">{it.left}</div>
            {it.right && <div className="text-zinc-500 mt-1 text-[11px]">→ {it.right}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// 3. Diff entre 2 révisions de doctrine (git-style)
// ============================================================================
export function DoctrineDiffViewer({ doctrineId, onClose }) {
  const [revisions, setRevisions] = useState([]);
  const [loadingRevs, setLoadingRevs] = useState(true);
  const [revA, setRevA] = useState("");
  const [revB, setRevB] = useState("");
  const [docA, setDocA] = useState(null);
  const [docB, setDocB] = useState(null);

  useEffect(() => {
    setLoadingRevs(true);
    api.get(`/workspace/doctrines/${doctrineId}/revisions`)
      .then((r) => {
        setRevisions(r.data.revisions || []);
        const revs = r.data.revisions || [];
        if (revs.length >= 2) {
          setRevA(revs[1].id);
          setRevB(revs[0].id);
        } else if (revs.length === 1) {
          setRevA(revs[0].id);
        }
      })
      .catch(() => setRevisions([]))
      .finally(() => setLoadingRevs(false));
  }, [doctrineId]);

  useEffect(() => {
    if (!revA) { setDocA(null); return; }
    api.get(`/workspace/doctrines/revisions/${revA}`).then((r) => setDocA(r.data)).catch(() => setDocA(null));
  }, [revA]);
  useEffect(() => {
    if (!revB) { setDocB(null); return; }
    api.get(`/workspace/doctrines/revisions/${revB}`).then((r) => setDocB(r.data)).catch(() => setDocB(null));
  }, [revB]);

  const diffField = (label, valA, valB) => {
    if (Array.isArray(valA) || Array.isArray(valB)) {
      const a = valA || [];
      const b = valB || [];
      const setA = new Set(a);
      const setB = new Set(b);
      const added = b.filter((x) => !setA.has(x));
      const removed = a.filter((x) => !setB.has(x));
      const unchanged = a.filter((x) => setB.has(x));
      if (added.length === 0 && removed.length === 0) {
        return (
          <div className="text-[10px] text-zinc-600 italic mb-3">{label} : aucun changement ({unchanged.length} items)</div>
        );
      }
      return (
        <div className="mb-4" data-testid={`diff-section-${label}`}>
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">{label}</div>
          <div className="space-y-1">
            {removed.map((it, i) => (
              <div key={`rm-${i}`} className="text-xs bg-red-500/10 border-l-2 border-red-500 pl-2 py-1 text-red-300 flex items-start gap-1">
                <Minus className="w-3 h-3 mt-0.5 flex-shrink-0" /> <span>{it}</span>
              </div>
            ))}
            {added.map((it, i) => (
              <div key={`add-${i}`} className="text-xs bg-emerald-500/10 border-l-2 border-emerald-500 pl-2 py-1 text-emerald-300 flex items-start gap-1">
                <Plus className="w-3 h-3 mt-0.5 flex-shrink-0" /> <span>{it}</span>
              </div>
            ))}
          </div>
          {unchanged.length > 0 && (
            <div className="text-[10px] text-zinc-600 mt-1">+ {unchanged.length} principes inchangés</div>
          )}
        </div>
      );
    }
    // String fields
    const a = valA || "";
    const b = valB || "";
    if (a === b) {
      return <div className="text-[10px] text-zinc-600 italic mb-3">{label} : inchangé</div>;
    }
    return (
      <div className="mb-4" data-testid={`diff-section-${label}`}>
        <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">{label}</div>
        {a && <div className="text-xs bg-red-500/10 border-l-2 border-red-500 pl-2 py-1 text-red-300 mb-1"><Minus className="w-3 h-3 inline mr-1" /> {a}</div>}
        {b && <div className="text-xs bg-emerald-500/10 border-l-2 border-emerald-500 pl-2 py-1 text-emerald-300"><Plus className="w-3 h-3 inline mr-1" /> {b}</div>}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="doctrine-diff-modal">
      <div className="noam-card max-w-3xl w-full max-h-[92vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <GitCompare className="w-4 h-4 text-[#7F77DD]" />
            <h3 className="text-base font-semibold text-zinc-100">Comparaison de révisions</h3>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white/10 rounded" data-testid="doctrine-diff-close">
            <XIcon className="w-4 h-4 text-zinc-400" />
          </button>
        </div>

        {loadingRevs && <div className="text-zinc-500 text-sm"><Loader2 className="w-3 h-3 animate-spin inline mr-1" /> Chargement…</div>}

        {!loadingRevs && revisions.length === 0 && (
          <div className="text-xs text-zinc-500 py-6 text-center">
            Aucune révision archivée. Modifiez la doctrine au moins une fois pour générer un historique.
          </div>
        )}

        {!loadingRevs && revisions.length > 0 && (
          <>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-zinc-500">Avant (rouge)</label>
                <select value={revA} onChange={(e) => setRevA(e.target.value)} className="w-full mt-1 bg-black/30 border border-white/10 rounded px-2 py-1.5 text-xs text-white" data-testid="diff-rev-a">
                  <option value="">— Choisir —</option>
                  {revisions.map((r) => (
                    <option key={r.id} value={r.id}>{`v${r.version} · ${(r.summary_of_change || (r.created_at || "").slice(0, 16)).slice(0, 50)}`}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-zinc-500">Après (vert)</label>
                <select value={revB} onChange={(e) => setRevB(e.target.value)} className="w-full mt-1 bg-black/30 border border-white/10 rounded px-2 py-1.5 text-xs text-white" data-testid="diff-rev-b">
                  <option value="">— Choisir —</option>
                  {revisions.map((r) => (
                    <option key={r.id} value={r.id}>{`v${r.version} · ${(r.summary_of_change || (r.created_at || "").slice(0, 16)).slice(0, 50)}`}</option>
                  ))}
                </select>
              </div>
            </div>

            {(docA || docB) && (
              <div className="border-t border-white/10 pt-3" data-testid="doctrine-diff-content">
                {diffField("Titre", docA?.title, docB?.title)}
                {diffField("Description", docA?.description, docB?.description)}
                {diffField("Tone", docA?.tone, docB?.tone)}
                {diffField("Principes", docA?.principles, docB?.principles)}
                {diffField("Filtres", docA?.filters, docB?.filters)}
                {diffField("Priorités", docA?.priorities, docB?.priorities)}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}


// ============================================================================
// Vague 17 — Drill-down d'une cellule de la heatmap (panneau latéral droit)
// ============================================================================
const CAT_LABEL_FULL = {
  LTV: "LTV (Loan-to-Value)",
  acteur: "Acteur",
  reglementaire: "Réglementaire",
  dossier: "Dossier",
  juridique: "Juridique",
  timing: "Timing",
  autre: "Autre",
};
const LEVEL_LABEL_FULL = {
  level_1: "Niveau 1 · Micro",
  level_2: "Niveau 2 · Spécialité",
  level_3: "Niveau 3 · Sous-secteur",
  level_4: "Niveau 4 · Secteur",
  level_5: "Niveau 5 · Macro",
  opportunite: "Opportunités",
};

export function FrictionCellDrillDown({ categorie, level, onClose, onPlanCreated }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showPlanForm, setShowPlanForm] = useState(false);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError("");
    api.get("/workspace/origination/frictions/heatmap/cell", { params: { categorie, level } })
      .then((r) => { if (mounted) setData(r.data); })
      .catch((e) => { if (mounted) setError(fmtError(e, "Erreur drill-down")); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [categorie, level]);

  // ESC pour fermer
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="heatmap-drilldown">
      <div className="flex-1 bg-black/55 backdrop-blur-[2px]" onClick={onClose} />
      <aside
        className="w-full sm:w-[480px] md:w-[560px] bg-[var(--noam-card-bg)] border-l border-white/10 overflow-y-auto shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-white/10 sticky top-0 bg-[var(--noam-card-bg)] z-10">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-[#EF9F27]">
                <Flame className="w-3 h-3" /> Drill-down cellule
              </div>
              <h3 className="text-base font-semibold text-zinc-100 mt-1 truncate">
                {CAT_LABEL_FULL[categorie] || categorie} × {LEVEL_LABEL_FULL[level] || level}
              </h3>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-white/10 rounded flex-shrink-0"
              data-testid="drilldown-close"
              aria-label="Fermer"
            >
              <XIcon className="w-4 h-4 text-zinc-400" />
            </button>
          </div>
        </div>

        <div className="flex-1 px-5 py-4 space-y-4">
          {loading && (
            <div className="text-zinc-500 text-sm py-8 text-center">
              <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Chargement…
            </div>
          )}
          {error && <div className="text-red-400 text-xs">{error}</div>}

          {data && !loading && (
            <>
              {/* Métriques de la cellule */}
              <div className="grid grid-cols-3 gap-2" data-testid="drilldown-metrics">
                <DrillMetric label="Frictions" value={data.items_count} color="#EF9F27" />
                <DrillMetric label="Score ⌀" value={data.avg_score || "—"} color="#7F77DD" />
                <DrillMetric
                  label="Coût/mois"
                  value={data.total_cost_monthly_meur ? `${data.total_cost_monthly_meur.toFixed(2)}M€` : "—"}
                  color="#EF4444"
                />
              </div>
              {data.total_cost_annual_meur > 0 && (
                <div className="text-[10px] text-zinc-500">
                  Soit ~<strong className="text-red-300">{data.total_cost_annual_meur.toFixed(1)} M€</strong> cumulé sur 12 mois si rien n'est fait.
                </div>
              )}

              {/* Frictions individuelles */}
              {data.items.length === 0 ? (
                <div className="text-xs text-zinc-500 py-4 text-center">Aucune friction détaillée dans cette cellule.</div>
              ) : (
                <div data-testid="drilldown-frictions">
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">
                    Frictions identifiées ({data.items.length})
                  </div>
                  <div className="space-y-2">
                    {data.items.map((it, i) => (
                      <div
                        key={`${it.snapshot_id}-${i}`}
                        className="border border-white/10 rounded p-3 bg-white/[0.02]"
                        data-testid={`drilldown-friction-${i}`}
                      >
                        <div className="flex items-start gap-2">
                          <span className="text-[#EF9F27] font-bold text-xs flex-shrink-0 w-6 text-center pt-0.5">
                            {it.score_friction}
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="text-xs text-zinc-200">{it.label}</div>
                            {it.comment_lever && (
                              <div className="text-[11px] text-zinc-500 mt-1 flex items-start gap-1">
                                <ArrowRight className="w-3 h-3 mt-0.5 flex-shrink-0 text-[#1D9E75]" />
                                <span>{it.comment_lever}</span>
                              </div>
                            )}
                            <div className="flex items-center gap-2 flex-wrap mt-1.5 text-[10px] text-zinc-500">
                              {it.opportunite_titre && (
                                <span className="px-1.5 py-0.5 rounded bg-[#378ADD]/10 border border-[#378ADD]/30 text-[#378ADD]">
                                  Opp · {it.opportunite_titre.slice(0, 40)}
                                </span>
                              )}
                              {it.fractale_level && (
                                <span className="px-1.5 py-0.5 rounded bg-[#A78BFA]/10 border border-[#A78BFA]/30 text-[#A78BFA]">
                                  Niveau {it.fractale_level}
                                </span>
                              )}
                              {it.cost_inaction_meur_monthly > 0 && (
                                <span className="text-red-300 font-medium">
                                  {it.cost_inaction_meur_monthly.toFixed(2)} M€/mois
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Opportunités sources */}
              {data.sources_opp && data.sources_opp.length > 0 && (
                <div data-testid="drilldown-sources-opp">
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">
                    Opportunités sources ({data.sources_opp.length})
                  </div>
                  <div className="space-y-1">
                    {data.sources_opp.map((o, i) => (
                      <div key={i} className="text-[11px] flex items-center gap-2 p-2 bg-white/[0.02] border border-white/5 rounded">
                        <ChevronRight className="w-3 h-3 text-[#378ADD] flex-shrink-0" />
                        <span className="text-zinc-200 flex-1 truncate">{o.titre}</span>
                        <span className="text-zinc-500 text-[10px]">{o.frictions_count} fr.</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Action footer */}
        {data && data.items_count > 0 && (
          <div className="px-5 py-4 pb-8 border-t border-white/10 bg-[var(--noam-card-bg)] sticky bottom-0 space-y-2">
            {!showPlanForm ? (
              <>
                <button
                  onClick={() => setShowPlanForm(true)}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-[#1D9E75] hover:bg-[#1D9E75]/90 text-white text-sm font-medium rounded transition"
                  data-testid="drilldown-launch-plan"
                >
                  <ListChecks className="w-4 h-4" />
                  Lancer un plan d'action
                </button>
                <p className="text-[10px] text-zinc-500 text-center">
                  Création d'un plan pré-rempli avec les frictions et le coût d'inaction.
                </p>
              </>
            ) : (
              <PlanInlineForm
                categorie={categorie}
                level={level}
                cellData={data}
                onCancel={() => setShowPlanForm(false)}
                onCreated={(plan) => {
                  setShowPlanForm(false);
                  notify.success(`Plan « ${plan.titre} » créé.`);
                  onPlanCreated?.(plan);
                }}
              />
            )}
          </div>
        )}
      </aside>
    </div>
  );
}

function DrillMetric({ label, value, color }) {
  return (
    <div className="bg-white/[0.02] border border-white/5 rounded p-2 text-center">
      <div className="text-[9px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className="text-sm font-bold mt-0.5 truncate" style={{ color }}>{value}</div>
    </div>
  );
}

function PlanInlineForm({ categorie, level, cellData, onCancel, onCreated }) {
  const navigate = useNavigate();
  const today = new Date();
  const in3m = new Date(today.getTime() + 90 * 24 * 3600 * 1000).toISOString().slice(0, 10);

  const defaultTitle = `Lever frictions ${CATEGORIES_LABEL[categorie] || categorie} · ${LEVEL_LABEL[level] || level}`;
  const [titre, setTitre] = useState(defaultTitle);
  const [owner, setOwner] = useState("");
  const [echeance, setEcheance] = useState(in3m);
  const [priorite, setPriorite] = useState(cellData?.total_cost_monthly_meur > 0.5 ? "P0" : "P1");
  const [budget, setBudget] = useState(0);
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (goToPlans) => {
    if (!titre.trim() || titre.trim().length < 3) {
      notify.error("Le titre est requis (3 caractères min).");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/workspace/origination/frictions/launch-plan", {
        categorie,
        level,
        titre: titre.trim(),
        owner: owner.trim(),
        echeance,
        priorite,
        budget_keur: Number(budget) || 0,
        description: description.trim() || null,
        include_friction_labels: true,
      });
      if (goToPlans) {
        // Naviguer AVANT onCreated pour éviter l'unmount du composant
        navigate("/app/plans");
        notify.success(`Plan « ${r.data.plan.titre} » créé.`);
      } else {
        onCreated?.(r.data.plan);
      }
    } catch (e) {
      notify.error(fmtError(e, "Création échouée"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-2.5" data-testid="drilldown-plan-form">
      <div className="text-[10px] uppercase tracking-wider text-[#1D9E75] flex items-center gap-1">
        <ListChecks className="w-3 h-3" /> Nouveau plan d'action
      </div>
      <input
        value={titre}
        onChange={(e) => setTitre(e.target.value)}
        placeholder="Titre du plan"
        className="w-full bg-black/30 border border-white/10 rounded px-2.5 py-1.5 text-xs text-white focus:border-[#1D9E75] focus:outline-none"
        data-testid="plan-form-titre"
      />
      <div className="grid grid-cols-2 gap-2">
        <input
          value={owner}
          onChange={(e) => setOwner(e.target.value)}
          placeholder="Owner (optionnel)"
          className="bg-black/30 border border-white/10 rounded px-2.5 py-1.5 text-xs text-white focus:border-[#1D9E75] focus:outline-none"
          data-testid="plan-form-owner"
        />
        <input
          type="date"
          value={echeance}
          onChange={(e) => setEcheance(e.target.value)}
          className="bg-black/30 border border-white/10 rounded px-2.5 py-1.5 text-xs text-white focus:border-[#1D9E75] focus:outline-none"
          data-testid="plan-form-echeance"
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <select
          value={priorite}
          onChange={(e) => setPriorite(e.target.value)}
          className="bg-black/30 border border-white/10 rounded px-2.5 py-1.5 text-xs text-white focus:border-[#1D9E75] focus:outline-none"
          data-testid="plan-form-priorite"
        >
          <option value="P0">P0 · Critique</option>
          <option value="P1">P1 · Important</option>
          <option value="P2">P2 · Normal</option>
        </select>
        <input
          type="number"
          min="0"
          value={budget}
          onChange={(e) => setBudget(e.target.value)}
          placeholder="Budget k€"
          className="bg-black/30 border border-white/10 rounded px-2.5 py-1.5 text-xs text-white focus:border-[#1D9E75] focus:outline-none"
          data-testid="plan-form-budget"
        />
      </div>
      <textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        rows={2}
        placeholder="Note additionnelle (les frictions et le coût d'inaction sont déjà injectés)"
        className="w-full bg-black/30 border border-white/10 rounded px-2.5 py-1.5 text-xs text-white focus:border-[#1D9E75] focus:outline-none resize-none"
        data-testid="plan-form-description"
      />
      <div className="flex items-center gap-2">
        <button
          onClick={onCancel}
          disabled={submitting}
          className="text-[11px] px-3 py-1.5 border border-white/10 text-zinc-400 hover:bg-white/5 rounded disabled:opacity-40"
          data-testid="plan-form-cancel"
        >
          Annuler
        </button>
        <button
          onClick={() => submit(false)}
          disabled={submitting}
          className="flex-1 text-[11px] px-3 py-1.5 bg-[#1D9E75] hover:bg-[#1D9E75]/90 text-white rounded disabled:opacity-40 inline-flex items-center justify-center gap-1.5"
          data-testid="plan-form-submit"
        >
          {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3" />}
          Créer le plan
        </button>
        <button
          onClick={() => submit(true)}
          disabled={submitting}
          title="Créer + naviguer vers le module Plans"
          className="text-[11px] px-3 py-1.5 bg-[#7F77DD] hover:bg-[#7F77DD]/90 text-white rounded disabled:opacity-40 inline-flex items-center gap-1"
          data-testid="plan-form-submit-and-go"
        >
          <ExternalLink className="w-3 h-3" />
          Créer & ouvrir
        </button>
      </div>
    </div>
  );
}


// ============================================================================
// Vague 18 — Couche fantômes superposée sur la heatmap
// ============================================================================
const PHANTOM_TYPE_COLORS = {
  ressource_pivot: "#EF4444",
  capture_economique: "#EF9F27",
  profondeur_causale: "#F4D35E",
  plan_action: "#A78BFA",
  reseau: "#378ADD",
};

function PhantomOverlayLayer({ data, onClose }) {
  const navigate = useNavigate();
  if (!data) return null;
  const types = data.types || [];
  const labels = data.type_labels || {};
  return (
    <div
      className="noam-card p-4 border-l-2 border-l-[#A78BFA]/50"
      data-testid="heatmap-phantom-overlay"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div>
          <div className="flex items-center gap-2 noam-overline text-[#A78BFA]">
            <Ghost className="w-3 h-3" /> Couche fantômes · superposée
          </div>
          <h3 className="text-sm font-semibold text-zinc-100 mt-1">Ce qui devrait être là et ne l'est pas</h3>
          <p className="text-[11px] text-zinc-500 mt-1 max-w-2xl">
            Agrégat des scans Phantom Detector sur l'ensemble des sujets (opportunités, entités, risques).
            La heatmap des frictions montre <em>les obstacles documentés</em> ; cette couche montre
            <em> les obstacles invisibles</em>.
          </p>
        </div>
        <button
          onClick={onClose}
          className="text-[10px] px-2 py-1 rounded border border-white/10 text-zinc-400 hover:text-zinc-200"
          data-testid="heatmap-phantom-close"
        >
          <EyeOff className="w-3 h-3 inline mr-1" />
          Masquer
        </button>
      </div>

      {data.total_phantoms === 0 ? (
        <div className="text-xs text-zinc-500 py-4 text-center border border-dashed border-white/10 rounded">
          Aucun fantôme détecté. Lancez un scan depuis une Cartographie 360° (opportunité, entité, risque).
        </div>
      ) : (
        <>
          {/* KPI strip */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3" data-testid="heatmap-phantom-kpis">
            <PhantomKPI label="Fantômes" value={data.total_phantoms} color="#A78BFA" />
            <PhantomKPI label="Sujets scannés" value={data.total_subjects_scanned} color="#7F77DD" />
            <PhantomKPI
              label="Coût/mois"
              value={data.total_cost_monthly_meur ? `${data.total_cost_monthly_meur.toFixed(2)} M€` : "—"}
              color="#EF4444"
            />
            <PhantomKPI
              label="Coût annuel"
              value={data.total_cost_annual_meur ? `${data.total_cost_annual_meur.toFixed(1)} M€` : "—"}
              color="#EF4444"
            />
            <PhantomKPI
              label="Critiques"
              value={data.by_severity?.critique || 0}
              color="#EF4444"
            />
          </div>

          {/* Mini matrice type × subject_type */}
          <div className="overflow-x-auto mb-3" data-testid="heatmap-phantom-matrix">
            <table className="w-full text-[11px]">
              <thead>
                <tr className="text-zinc-500 text-[9px] uppercase tracking-wider border-b border-white/10">
                  <th className="text-left p-2">Type fantôme</th>
                  {(data.subject_types || []).map((st) => (
                    <th key={st} className="text-center p-2">{st === "opportunite" ? "Opp" : st === "entite" ? "Entité" : "Risque"}</th>
                  ))}
                  <th className="text-center p-2 text-[#A78BFA]">Total</th>
                  <th className="text-center p-2 text-red-300">Coût/m</th>
                </tr>
              </thead>
              <tbody>
                {types.map((t, idx) => (
                  <tr key={t}>
                    <td className="p-2 flex items-center gap-1.5">
                      <span
                        className="w-4 h-4 rounded text-[9px] flex items-center justify-center font-bold flex-shrink-0"
                        style={{ background: `${PHANTOM_TYPE_COLORS[t]}33`, color: PHANTOM_TYPE_COLORS[t] }}
                      >{idx + 1}</span>
                      <span className="text-zinc-200">{labels[t] || t}</span>
                    </td>
                    {(data.subject_types || []).map((st) => {
                      const cell = data.matrix?.[t]?.[st] || { count: 0, cost_monthly: 0 };
                      return (
                        <td
                          key={st}
                          className={`p-2 text-center ${cell.count > 0 ? "text-zinc-200" : "text-zinc-600"}`}
                        >
                          {cell.count > 0 ? (
                            <div>
                              <div className="font-bold">{cell.count}</div>
                              {cell.cost_monthly > 0 && (
                                <div className="text-[9px] text-red-300">{cell.cost_monthly.toFixed(2)}M€</div>
                              )}
                            </div>
                          ) : "·"}
                        </td>
                      );
                    })}
                    <td className="p-2 text-center text-[#A78BFA] font-bold">{data.by_type?.[t] || 0}</td>
                    <td className="p-2 text-center text-red-300">
                      {(data.by_type_cost_monthly?.[t] || 0) > 0 ? `${data.by_type_cost_monthly[t].toFixed(2)}` : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Top sujets */}
          {data.top_subjects && data.top_subjects.length > 0 && (
            <div data-testid="heatmap-phantom-top">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">
                Top sujets silencieux ({data.top_subjects.length})
              </div>
              <div className="space-y-1">
                {data.top_subjects.slice(0, 5).map((s, i) => (
                  <button
                    key={i}
                    onClick={() => navigate(`/app/cartographie/${s.subject_type}/${s.subject_id}`)}
                    className="w-full text-left text-[11px] flex items-center gap-2 p-2 bg-white/[0.02] border border-white/5 rounded hover:border-[#A78BFA]/40 transition"
                    data-testid={`heatmap-phantom-top-${i}`}
                  >
                    <span className="text-[#A78BFA] font-bold w-8 text-center">{s.phantom_score}</span>
                    <span className="text-zinc-500 text-[10px] uppercase tracking-wider w-12">
                      {s.subject_type === "opportunite" ? "Opp" : s.subject_type === "entite" ? "Entité" : "Risque"}
                    </span>
                    <span className="text-zinc-200 flex-1 truncate">{s.subject_label || "(sans nom)"}</span>
                    <span className="text-zinc-500 text-[10px]">{s.phantoms_count} fantômes</span>
                    {s.total_cost_monthly_meur > 0 && (
                      <span className="text-red-300 text-[10px]">{s.total_cost_monthly_meur.toFixed(2)} M€/m</span>
                    )}
                    <ChevronRight className="w-3 h-3 text-zinc-500" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function PhantomKPI({ label, value, color }) {
  return (
    <div className="bg-white/[0.02] border border-white/5 rounded p-2 text-center">
      <div className="text-[9px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className="text-sm font-bold mt-0.5 truncate" style={{ color }}>{value}</div>
    </div>
  );
}
